import { Stats } from "@/components/dashboard/stats.tsx";
import { MonthPicker } from "@/components/ui/month-picker.tsx";

import { useDashboardStats } from "@/lib/api/dashboard.ts";
import { useGroups } from "@/lib/api/group";
import { useDashboardParams } from "@/lib/hooks/admin/use-dashboard-params.ts";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { useClearCache } from "@/lib/api/admin";
import { ExportDataDialog } from "./export-data-dialog";
import { MultiSelect } from "../ui/multi-select";
import { Link } from "@tanstack/react-router";
import { useAbility } from "@/lib/casl/ability-context";

export function OverallHealth() {
  const { month, groups, last7Days } = useDashboardParams();

  const { data, isPending, isError } = useDashboardStats(
    month,
    groups,
    last7Days,
  );

  return (
    <div className="">
      <h2 className="my-6 text-2xl font-bold leading-7 text-black">
        Overall Health
      </h2>

      <Filters />

      {isPending ? (
        <div className="inline-flex h-24 w-full items-center justify-center rounded-xl border border-neutral-200">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : isError || !data ? (
        <div className="inline-flex h-24 w-full items-center justify-center rounded-xl border border-neutral-200">
          <span className="font-normal leading-tight text-black">
            Some errors occurred!
          </span>
        </div>
      ) : (
        <Stats data={data} />
      )}
    </div>
  );
}

function Filters() {
  const { handleFilterChange, month, groups, handleMultipleFiltersChange } =
    useDashboardParams();
  const { data: groupsData } = useGroups();
  const ability = useAbility();

  const { mutateAsync: clearCache, isPending: isClearingCache } =
    useClearCache();

  return (
    <div className="grid grid-cols-5 grid-rows-1 items-center gap-3 px-1 py-3">
      <MonthPicker
        month={month}
        setMonth={(newMonth) => {
          if (newMonth === -1) {
            handleMultipleFiltersChange({ last7Days: "true", month: "-1" });
          } else {
            handleMultipleFiltersChange({
              last7Days: "false",
              month: "" + newMonth,
            });
          }
        }}
        showLast7Days
        showLast30Days
      />

      <MultiSelect
        options={
          groupsData?.map(({ id, name }) => ({
            value: id,
            label: name.replaceAll("_", " ").replaceAll("-", " "),
          })) || []
        }
        defaultValue={
          groups?.map((group) => ({ value: group, label: group })) || []
        }
        onChange={(newGroups) => {
          handleFilterChange(
            "groups",
            newGroups.map((group) => group.value),
          );
        }}
        className="w-full"
        placeholder="Select Groups"
      />

      <div></div>
      <div></div>

      <div className="flex items-center gap-2 justify-self-end">
        <Button
          variant="ghost"
          onClick={() => {
            clearCache();
          }}
        >
          {isClearingCache && <Loader2 className="h-5 w-5 animate-spin" />}
          Clear cache
        </Button>
        <ExportDataDialog />
        {ability.can("read", "exports") && (
          <Button variant="outline" asChild>
            <Link to="/exports">View Exports</Link>
          </Button>
        )}
      </div>
    </div>
  );
}
